<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable ref="rtable" :settings="{hideTable: true, hideExport: true, title: t('employeesCommissions')}"
                  :columns="columnDefs"
                  @onDataLoaded="onDataLoaded"
                  endpoint="reports/hr/employee-commissions">
      <template #filter>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="fullName"
            :placeholder="t('selectEmployee')"
            v-model="selectedEmployee"
            @change="onSelectedEmployeeChanged"
            remote
            :remote-method="searchEmployees"
        >
          <el-option
              v-for="option in employees"
              :key="option.id"
              :label="option.fullName"
              :value="option"
          ></el-option>
        </el-select>
      </template>
      <template #chart="slotProps">
        <div class="grid grid-cols-2 gap-2">
          <div class="col-span-12 md:col-span-2 chart-card" style="height: 300px">
            <v-chart :option="option" autoresize />
          </div>
        </div>
        <el-table :data="slotProps.data" style="width: 100%"
                  header-row-class-name="reports-table-header">
          <el-table-column type="expand">
            <template #default="props">
              <div class="ml-10">
                <el-table :data="props.row.orderItems"
                          header-row-class-name="reports-table-header">
                  <el-table-column :label="t('#')" type="index"/>
                  <el-table-column :label="t('item')" prop="itemName"/>
                  <el-table-column :label="t('dateAndTime')" prop="createdDate">
                    <template #default="props">
                      <div style="direction: ltr">
                        {{ formatPostgresDate(props.row.createdDate, 'DD/MM/YYYY hh:mm:ss A') }}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column width="120" :label="t('orderNumber')"
                                   prop="orderNumber"/>
                  <el-table-column width="80" :label="t('qty')" prop="qty"/>
                  <el-table-column :label="t('finalPrice')" prop="finalPrice">
                    <template #default="props">
                      {{ formatMoney(props.row.finalPrice, 'IQD') }}
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('commissionName')"
                                   prop="commissionName"/>
                  <el-table-column :label="t('rule')"
                                   prop="commissionType">
                    <template #default="props">
                      <div v-if="props.row.commissionType == 'amount'">
                        {{ formatMoney(props.row.commissionValue, 'IQD') }}
                      </div>
                      <div v-if="props.row.commissionType == 'percentage'">
                        {{`${props.row.commissionValue}%`}}
                      </div>
                    </template>
                  </el-table-column>
                  <el-table-column :label="t('commissionValue')"
                                   prop="commissionValue">
                    <template #default="props">
                      {{ formatMoney(props.row.amount, 'IQD') }}
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="t('employeeName')" prop="employeeName"/>
          <el-table-column :label="t('totalEligibleSales')"
                           prop="totalEligibleSales">
            <template #default="props">
              {{ formatMoney(props.row.totalEligibleSales, 'IQD') }}
            </template>
          </el-table-column>
          <el-table-column :label="t('commission')" prop="commission">
            <template #default="props">
              <div class="font-bold text-blue-500">
                {{ formatMoney(props.row.commission, 'IQD') }}
              </div>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import {onBeforeMount, provide, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {ColDef} from 'ag-grid-community'
import {useRoute, useRouter} from 'vue-router'
import {useHTTP} from '@/plugins/http'
import {useAlertModal} from '@/plugins/alert-modal/alert-modal'
import {formatMoney} from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import {formatPostgresDate} from '@/utils/date'
import User from '@/models/user/User'
import {commissionI18nMessages} from "@/models/hr/Commission";
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart } from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

// i18n
const {t} = useI18n({
  messages: {
    en: {
      ...commissionI18nMessages.en,
      employeesCommissions: 'Employees Commissions',
      selectEmployee: 'Select Employee',
      employeeName: 'Employee Name',
      totalEligibleSales: 'Total eligible sales',
      commission: 'Commission',
      item: 'Item',
      dateAndTime: 'Date & Time',
      orderNumber: 'Order Number',
      qty: 'Qty',
      finalPrice: 'Final Price',
      commissionName: 'Commission Name',
      commissionRule: 'Commission Rule',
      commissionValue: 'Commission Value',
      rule: 'Rule',

    },
    ar: {
      ...commissionI18nMessages.ar,
      employeesCommissions: 'عمولات الموظفين',
      selectEmployee: 'اختر الموظف',
      employeeName: 'اسم الموظف',
      totalEligibleSales: 'إجمالي المبيعات المؤهلة',
      commission: 'العمولة',
      item: 'المنتج',
      dateAndTime: 'التاريخ والوقت',
      orderNumber: 'رقم الطلب',
      qty: 'الكمية',
      finalPrice: 'السعر النهائي',
      commissionName: 'اسم العمولة',
      commissionRule: 'قاعدة العمولة',
      commissionValue: 'قيمة العمولة',
      rule: 'القاعدة',

    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()


use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')
const option = ref({
  title: {
    left: 'left'
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'axis',
    axisPointer: {
      type: 'shadow'
    }
  },
  legend: {},
  grid: {
    left: '3%',
    right: '4%',
    bottom: '3%',
    containLabel: true
  },

  xAxis: {
    type: 'category',
    data: []
  },
  yAxis: {
    type: 'value',
  },
  series: [
    {
      type: 'bar',
      barWidth: 50,
      itemStyle: {
        color: '#4285F4'
      },
      z: 100,
      data: []
    }
  ]
})

const employees = ref<User[] | any>();
const selectedEmployee = ref<any>()

onBeforeMount(async () => {
  columnDefs.value = []
})


async function onSelectedEmployeeChanged(event: any) {
  rtable.value.payload.filter = selectedEmployee.value ? selectedEmployee.value.id : null;
  rtable.value.navigate(rtable.value.payload)
}

async function searchEmployees(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${User.ENDPOINT}?q=${query}`
    const employeesRes = await http.get<User[]>(url)
    employees.value = employeesRes.map((employee) => User.from(employee))
  } catch (error) {
    employees.value = []
  }
}

function onDataLoaded(data: any) {
  const chartData = data.slice(0, 10)
  option.value.series[0].data = chartData.slice().map((i: any) => i.commission)
  option.value.xAxis.data = chartData.slice().map((i: any) => String(i.employeeName).slice(0, 15))
}
</script>
